import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

function resolveSearchDestination(query, orgId) {
  if (!orgId || !query || !query.trim()) return null;
  const q = query.trim().toLowerCase();
  const base = `/org/${orgId}`;

  if (/\bphishing\b|phish/i.test(q)) {
    return { path: `${base}/simulations`, search: '?q=phishing' };
  }
  if (/\bengenharia\b|eng\.?\s*social|social\s*eng/i.test(q) || q === 'chat') {
    return { path: `${base}/social-engineering-chat`, search: '' };
  }
  if (/\bdashboard\b|painel/i.test(q)) {
    return { path: `${base}/dashboard`, search: '' };
  }
  if (/\bhistórico\b|histórico|history/i.test(q)) {
    return { path: `${base}/history`, search: '' };
  }
  if (/\bavaliação\b|assessment|avaliar/i.test(q)) {
    return { path: `${base}/assessment`, search: '' };
  }
  if (/\borganiza\w*\b|organization/i.test(q)) {
    return { path: `${base}/organization`, search: '' };
  }
  if (/\bforça\s*bruta\b|brute/i.test(q)) {
    return { path: `${base}/simulations`, search: '?q=brute' };
  }
  if (/\brelatório\b|report/i.test(q)) {
    return { path: `${base}/history`, search: '' };
  }
  if (/\bsimula/i.test(q)) {
    return { path: `${base}/simulations`, search: q ? `?q=${encodeURIComponent(q)}` : '' };
  }

  return { path: `${base}/simulations`, search: `?q=${encodeURIComponent(q)}` };
}

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

export default function GlobalSearch({ orgId, className = '', expandable = true, expandLeft = false }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const SUGGESTIONS = t.searchSuggestions || ['phishing', 'dashboard', 'simulations', 'organization', 'assessment', 'history'];
  const [value, setValue] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (expanded) inputRef.current?.focus();
  }, [expanded]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestionsOpen(false);
        if (expandable) setExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandable]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch();
    }
    if (e.key === 'Escape') {
      setValue('');
      setSuggestionsOpen(false);
      if (expandable) setExpanded(false);
      inputRef.current?.blur();
    }
  };

  const executeSearch = () => {
    const dest = resolveSearchDestination(value, orgId);
    if (dest) {
      navigate(dest.path + dest.search);
      setValue('');
      setSuggestionsOpen(false);
      if (expandable) setExpanded(false);
    }
  };

  const filteredSuggestions = value.trim().length > 0
    ? SUGGESTIONS.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
    : SUGGESTIONS;

  if (!expandable) {
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 pointer-events-none">
            <SearchIcon />
          </span>
          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setSuggestionsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={t.searchPlaceholder}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none transition-colors"
          />
        </div>
        {suggestionsOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 py-1 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg z-50 overflow-hidden">
            {filteredSuggestions.length > 0 ? (
              filteredSuggestions.slice(0, 6).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    const dest = resolveSearchDestination(s, orgId);
                    if (dest) { navigate(dest.path + dest.search); setValue(''); setSuggestionsOpen(false); }
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-slate-900 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-cyan-500/15 transition-colors"
                >
                  {s}
                </button>
              ))
            ) : (
              <p className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">{t.pressEnterToSearch}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  const transitionClass = 'transition-all duration-[200ms] ease-out';

  return (
    <div ref={containerRef} className={`relative flex items-center justify-end ${className}`}>
      <div
        className={`flex items-center overflow-hidden rounded-lg border border-slate-300 dark:border-slate-700 ${transitionClass} ${
          expanded
            ? `w-56 sm:w-64 bg-slate-50 dark:bg-slate-800 shadow-sm ${expandLeft ? 'flex-row-reverse' : ''}`
            : 'w-10 bg-slate-50 dark:bg-slate-800'
        }`}
      >
        {!expanded ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className={`flex items-center justify-center w-10 h-10 text-slate-500 dark:text-slate-400 hover:text-cyan-500 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg ${transitionClass}`}
            aria-label={t.search}
          >
            <SearchIcon />
          </button>
        ) : (
          <>
            <span className={`flex-shrink-0 text-slate-500 dark:text-slate-400 ${expandLeft ? 'pr-3' : 'pl-3'}`}>
              <SearchIcon />
            </span>
            <input
              ref={inputRef}
              type="search"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onFocus={() => setSuggestionsOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={t.searchPlaceholder}
              className={`flex-1 min-w-0 py-2 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none ${expandLeft ? 'pl-4 pr-2' : 'pl-2 pr-4'}`}
            />
          </>
        )}
      </div>

      {expanded && suggestionsOpen && (
        <div
          className={`absolute top-full mt-1 py-1 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg z-50 overflow-hidden min-w-[200px] ${
            expandLeft ? 'right-0 left-auto' : 'left-0 right-0'
          }`}
        >
          {filteredSuggestions.length > 0 ? (
            filteredSuggestions.slice(0, 6).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  const dest = resolveSearchDestination(s, orgId);
                  if (dest) {
                    navigate(dest.path + dest.search);
                    setValue('');
                    setSuggestionsOpen(false);
                    setExpanded(false);
                  }
                }}
                className="w-full px-4 py-2 text-left text-sm text-slate-900 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-cyan-500/15 transition-colors"
              >
                {s}
              </button>
            ))
          ) : (
            <p className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">{t.pressEnterToSearch}</p>
          )}
        </div>
      )}
    </div>
  );
}
